import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import InputField from "components/fields/InputField";
import { adminLogin } from "api/adapters/auth/auth";
import { setAdminProfile } from "store/slices/authSlice";
import { setToken } from "utils/cookies.helpers";
import { validateEmail } from "utils/validators";

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [sEmail, setSEmail] = useState("");
  const [sPassword, setSPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const successMessage = location.state?.message || "";

  const { mutate: login, isPending } = useMutation({
    mutationFn: adminLogin,
    onSuccess: (res) => {
      const { sToken, _id, sUserName, eAdminType, sProfilePicUrl } = res.data;
      setToken(sToken);
      dispatch(
        setAdminProfile({ _id, sUserName, eAdminType, sProfilePicUrl, sEmail }),
      );
      navigate("/admin/dashboard");
    },
    onError: (err) => {
      setError(err.message || "Login failed. Please try again.");
    },
  });

  const handleLogin = (e) => {
    e.preventDefault();
    const eErr = validateEmail(sEmail);
    if (eErr) { setEmailError(eErr); return; }
    if (!sPassword) { setError("Password is required."); return; }
    setError("");
    login({ sEmail, sPassword });
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="w-full max-w-[400px] flex-col items-center">
        {/* TINKA LOGO */}
        <div className="flex flex-col items-center justify-center text-center">
          <h1
            className="text-[52px] font-black leading-none tracking-tight"
            style={{ color: "#FF7A00", fontFamily: "'Impact', 'Arial Black', sans-serif" }}
          >
            TINKA
          </h1>
          <p className="mt-3 text-sm font-semibold text-gray-800 dark:text-gray-300">
            Your trading reputation
          </p>
          <div className="mt-1 flex items-center justify-center space-x-1">
            <span className="text-xs font-bold text-[#8A2BE2]">Exchange-verified</span>
            <svg className="w-4 h-4 text-[#8A2BE2]" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <form className="mt-10 flex flex-col" onSubmit={handleLogin}>
          {successMessage && (
            <p className="mb-4 rounded-lg bg-green-50 px-4 py-2 text-sm font-medium text-green-600">
              {successMessage}
            </p>
          )}

          <InputField
            variant="auth"
            extra="mb-1"
            label="Email*"
            placeholder="admin@example.com"
            id="sEmail"
            type="email"
            value={sEmail}
            state={emailError ? "error" : undefined}
            onChange={(e) => {
              setSEmail(e.target.value);
              setEmailError(validateEmail(e.target.value));
              setError("");
            }}
          />
          {emailError && (
            <p className="mb-3 ml-1.5 text-xs font-medium text-red-500">{emailError}</p>
          )}
          {!emailError && <div className="mb-3" />}

          <InputField
            variant="auth"
            extra="mb-4"
            label="Password*"
            placeholder="Min. 8 characters"
            id="sPassword"
            type="password"
            value={sPassword}
            onChange={(e) => {
              setSPassword(e.target.value);
              setError("");
            }}
          />

          {error && (
            <p className="mb-3 text-sm font-medium text-red-500">{error}</p>
          )}

          <div className="mb-6 flex items-center justify-end">
            <a
              className="text-sm font-medium text-[#FF7A00] hover:underline"
              href="/auth/forgot-password"
            >
              Forgot Password?
            </a>
          </div>

          <button
            type="submit"
            disabled={isPending || !!emailError || !sEmail || !sPassword}
            className="mt-2 flex w-full items-center justify-center rounded-xl bg-[#FF7A00] py-[12px] text-base font-bold text-white transition duration-200 hover:bg-[#e66a00] active:bg-[#cc5e00] disabled:opacity-60 shadow-[0_4px_14px_0_rgba(255,122,0,0.39)]"
          >
            {isPending ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-10 text-center text-xs font-semibold text-gray-600 dark:text-gray-400">
          <p>Join traders showcasing</p>
          <p>their verified performance</p>
          <p className="mt-2 block font-bold text-[#FF7A00]">
            Discover Tinka
          </p>
        </div>
      </div>
    </div>
  );
}
